"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __exportStar = (this && this.__exportStar) || function(m, exports) {
    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useMonitoring = exports.MonitoringProvider = exports.Context = void 0;
var Sentry = __importStar(require("@sentry/browser"));
var tracing_1 = require("@sentry/tracing");
var react_1 = __importStar(require("react"));
var types_1 = require("./types");
__exportStar(require("./types"), exports);
/**
 * Monitoring context which will create wrapper for monitoring functionality.
*/
exports.Context = (0, react_1.createContext)({
    setMonitoringUser: function () { },
    errorHandler: function () { return ''; },
    measurePerformance: function () { return ({
        setTag: function () { },
        startSpan: function () { },
        finishSpan: function () { },
        finish: function () { },
    }); },
    setMeasurement: function () { },
});
/**
 * Monitoring wrapper used to abstract Sentry functionality.
 *
 * @param {MonitoringProps} data - Configuration for sentry to override default configuration.
 * @return {React.ReactNode}
*/
function MonitoringProvider(_a) {
    var children = _a.children, config = _a.config;
    (0, react_1.useEffect)(function () {
        var _a, _b, _c, _d;
        if (config) {
            Sentry.init({
                dsn: config.dsn,
                environment: (_a = config.environment) !== null && _a !== void 0 ? _a : 'local',
                debug: (_b = config.debug) !== null && _b !== void 0 ? _b : true,
                release: (_c = config.release) !== null && _c !== void 0 ? _c : '1.0',
                tracesSampleRate: (_d = config.tracesSampleRate) !== null && _d !== void 0 ? _d : 0.025,
                beforeBreadcrumb: function (breadcrumb, hint) {
                    if (breadcrumb.category === 'xhr') {
                        return null;
                    }
                    return breadcrumb;
                },
                integrations: [
                    new tracing_1.BrowserTracing({ tracePropagationTargets: config.tracingOrigins }),
                ],
            });
            if (config.customTags) {
                Sentry.setTags(config.customTags);
            }
        }
    }, [config]);
    /**
     * Updates user context information for future events.
     *
     * @param id {string} set user for in sentry
     * @return {void}
    */
    var setMonitoringUser = (0, react_1.useCallback)(function (id) {
        Sentry.setUser({ id: id });
    }, []);
    /**
     * Set key:value that will be sent as tags data with the event.
     *
     * Can also be used to unset a tag, by passing `undefined`.
     *
     * @param key String key of tag
     * @param value Value of tag
     * @return {void}
    */
    var setMonitoringTag = (0, react_1.useCallback)(function (key, value) {
        Sentry.setTag(key, value);
    }, []);
    /**
     * Error handler function which is used to capture errors in sentry.
     *
     * @param error {Error | string} - Caught error that to be send to Sentry.io
     * @returns {string | null}
     */
    var errorHandler = (0, react_1.useCallback)(function (error) {
        if (!Sentry) {
            return null;
        }
        return Sentry.captureException(error);
    }, []);
    /**
     * Measure the performance of application based on functionality and operation based on it.
     * Return type of the function is the IIFE, which will helps to close the transaction and complete the measurement.
     *
     * @param name {string} - Name of transaction
     * @param operation {string} - Operation of transaction to be performed
     * @param [data] {{[key: string]: number | string}} - Data to be added on transaction
     * @returns {SentryTransactionObject} - Which will helps to close the transaction and complete the measurement.
    */
    var measurePerformance = (0, react_1.useCallback)(function (name, op, data) {
        // This will create a new Transaction
        var transaction = Sentry.startTransaction({ name: name, data: data, op: op, sampled: true });
        // Set transaction on scope to associate with errors and get included span instrumentation
        // If there's currently an unfinished transaction, it may be dropped
        // Sentry.getCurrentHub().configureScope((scope) => scope.setSpan(transaction));
        // Create an object to map spans for a transaction
        var transactionSpansObj = {};
        return {
            setTag: function (tagName, tagValue) { return transaction.setTag(tagName, tagValue); },
            startSpan: function (spanOp, spanData) {
                transactionSpansObj[spanOp] = transaction.startChild({ op: spanOp, data: spanData });
            },
            finishSpan: function (spanOp) {
                if (transactionSpansObj[spanOp]) {
                    var spanObj = transactionSpansObj[spanOp];
                    spanObj.setStatus(types_1.MonitoringStatus.OK);
                    spanObj.finish();
                    delete transactionSpansObj[spanOp];
                }
            },
            finish: function (status) {
                if (status === void 0) { status = types_1.MonitoringStatus.OK; }
                transaction.setStatus(status);
                transaction.finish();
            },
        };
    }, []);
    /**
     * Set the custom measurement on particular transaction
     *
     * @param transactionName Name of the transaction
     * @param name Name of the measurement
     * @param value Value of the measurement
     * @param [unit] Unit of the measurement. (Defaults to an empty string)
     * @return {void}
     */
    var setMeasurement = (0, react_1.useCallback)(function (transactionName, name, value, unit) {
        var transaction = Sentry.startTransaction({ name: transactionName, op: name, sampled: true });
        setTimeout(function () {
            transaction.setMeasurement(name, value, unit);
            transaction.setMeasurement('frames_total', value, unit);
            transaction.setStatus(types_1.MonitoringStatus.OK);
            transaction.finish();
        }, 100);
    }, []);
    var monitoringContextValue = (0, react_1.useMemo)(function () { return ({ setMonitoringUser: setMonitoringUser, setMonitoringTag: setMonitoringTag, errorHandler: errorHandler, measurePerformance: measurePerformance, setMeasurement: setMeasurement }); }, [setMonitoringUser, setMonitoringTag, errorHandler, measurePerformance, setMeasurement]);
    return (react_1.default.createElement(exports.Context.Provider, { value: monitoringContextValue }, children));
}
exports.MonitoringProvider = MonitoringProvider;
/**
 * Custom hook which will provide monitoring context which will expose all the functionality.
*/
function useMonitoring() {
    return (0, react_1.useContext)(exports.Context);
}
exports.useMonitoring = useMonitoring;
//# sourceMappingURL=index.js.map